import { createContext, useContext, useRef, type ReactNode } from "react";
import { useToast } from "@mbbe/ui";
import { useLanguage } from "../contexts/LanguageContext";
import { portalCopy } from "./copy";
import { getCustomerProfile, updateCustomerLanguage } from "./lib/customerApi";
import {
  createLanguageSyncController,
  type LanguageSyncController,
} from "./lib/languagePreference";

const PortalLanguagePreferenceContext = createContext<LanguageSyncController | null>(null);

export const LANGUAGE_SAVE_ERROR_TOAST_ID = "portal-language-save-error";

/** The single owner of portal language selection and its account persistence. */
export function PortalLanguagePreferenceProvider({ children }: { children: ReactNode }) {
  const { language, setLanguage } = useLanguage();
  const toast = useToast();
  const latest = useRef({ language, setLanguage, toast });
  latest.current = { language, setLanguage, toast };
  const controller = useRef<LanguageSyncController | null>(null);
  if (!controller.current) {
    controller.current = createLanguageSyncController({
      save: (next) => updateCustomerLanguage(next),
      readServerLanguage: async () => (await getCustomerProfile()).language,
      getVisible: () => latest.current.language,
      apply: (next) => latest.current.setLanguage(next),
      onFinalFailure: ({ restored }) =>
        latest.current.toast.error({
          id: LANGUAGE_SAVE_ERROR_TOAST_ID,
          title: portalCopy[restored].languageSaveError,
        }),
    });
  }
  return (
    <PortalLanguagePreferenceContext.Provider value={controller.current}>
      {children}
    </PortalLanguagePreferenceContext.Provider>
  );
}

export function useOptionalPortalLanguagePreference() {
  return useContext(PortalLanguagePreferenceContext);
}

export function usePortalLanguagePreference() {
  const context = useContext(PortalLanguagePreferenceContext);
  if (!context) throw new Error("usePortalLanguagePreference must be used within a PortalLanguagePreferenceProvider");
  return context;
}
